import { z } from 'zod';
import type { DoctorCreateInput } from '../types/prisma';

/**
 * Схема валидации для создания/редактирования врача
 */
export const doctorSchema = z.object({
  slug: z
    .string()
    .optional()
    .refine((val) => !val || /^[a-z0-9-]+$/.test(val), {
      message: 'Slug может содержать только строчные буквы, цифры и дефисы'
    }),
  fullName: z
    .string()
    .min(1, 'Полное имя обязательно для заполнения')
    .min(2, 'Полное имя должно содержать минимум 2 символа')
    .max(100, 'Полное имя не должно превышать 100 символов'),
  specialization: z
    .string()
    .min(1, 'Специализация обязательна для заполнения')
    .min(2, 'Специализация должна содержать минимум 2 символа')
    .max(100, 'Специализация не должна превышать 100 символов'),
  description: z
    .string()
    .min(1, 'Описание обязательно для заполнения')
    .min(10, 'Описание должно содержать минимум 10 символов')
    .max(2000, 'Описание не должно превышать 2000 символов'),
  educationPrimary: z
    .string()
    .min(1, 'Основное образование обязательно для заполнения')
    .min(5, 'Основное образование должно содержать минимум 5 символов')
    .max(500, 'Основное образование не должно превышать 500 символов'),
  educationAdvanced: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? '')
    .pipe(z.string().max(500, 'Дополнительное образование не должно превышать 500 символов')),
  interestsArea: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? '')
    .pipe(z.string().max(500, 'Области интересов не должны превышать 500 символов')),
  principles: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? '')
    .pipe(z.string().max(500, 'Принципы не должны превышать 500 символов')),
  experience: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? '')
    .pipe(z.string().max(500, 'Опыт работы не должен превышать 500 символов')),
  /*
    Год, а не число лет: число пришлось бы править каждый январь. Пустое поле
    допустимо — тогда плашка со стажем на карточке просто не показывается.
  */
  practiceSinceYear: z
    .union([z.number(), z.string()])
    .nullable()
    .optional()
    .transform((val) => {
      if (val === null || val === undefined || val === '') return null;
      const n = typeof val === 'number' ? val : Number(val);
      return Number.isFinite(n) ? n : null;
    })
    .refine((val) => val === null || (Number.isInteger(val) && val >= 1950 && val <= 2100), {
      message: 'Укажите год начала практики, например 2014'
    }),
  onSiteBookingUrl: z
    .string()
    .optional()
    .refine((val) => !val || z.string().url().safeParse(val).success, {
      message: 'Введите корректный URL для записи в клинике'
    }),
  proDoctorovReviewsWidgetCode: z
    .string()
    .nullable()
    .optional()
    .transform((val) => val ?? ''),
  isActive: z.boolean().default(true),
  isAvailableOnline: z.boolean().default(false),
  isAvailableAtHome: z.boolean().default(false),
  isAvailableInTheOffice: z.boolean().default(false),
  sortOrder: z
    .number()
    .int('Порядок сортировки должен быть целым числом')
    .min(0, 'Порядок сортировки не может быть отрицательным')
    .default(0)
});

/**
 * Тип данных формы врача
 * Основан на Zod схеме для валидации форм
 */
export type DoctorFormData = z.infer<typeof doctorSchema>;

/**
 * Тип для создания врача через API
 * Использует 100% Prisma-generated тип
 */
export type DoctorCreateData = Pick<
  DoctorCreateInput,
  | 'slug'
  | 'fullName'
  | 'specialization'
  | 'description'
  | 'educationPrimary'
  | 'educationAdvanced'
  | 'interestsArea'
  | 'principles'
  | 'experience'
  | 'practiceSinceYear'
  | 'onSiteBookingUrl'
  | 'proDoctorovReviewsWidgetCode'
  | 'isActive'
  | 'isAvailableOnline'
  | 'isAvailableAtHome'
  | 'sortOrder'
>;

/**
 * Схема валидации для загрузки изображения врача
 */
export const doctorImageSchema = z.object({
  file: z
    .instanceof(File)
    .refine((file) => file.size <= 5 * 1024 * 1024, {
      message: 'Размер файла не должен превышать 5MB'
    })
    .refine(
      (file) =>
        ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(
          file.type
        ),
      {
        message: 'Разрешены только файлы JPG, PNG и WebP'
      }
    )
});

/**
 * Тип данных формы загрузки изображения
 */
export type DoctorImageFormData = z.infer<typeof doctorImageSchema>;
